import { http } from "../../utils/config";

const articleAPI = {
  // Get articles for Admin
  getArticles: (params = {}) => {
    return http.get("/api/v1/articles", { params });
  },

  // Get article by ID
  getArticleById: (id) => {
    return http.get(`/api/v1/articles/${id}`);
  },

  // Create article (uses FormData for thumbnail file)
  createArticle: (formData) => {
    return http.post("/api/v1/articles", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  },

  // Update article (uses FormData for thumbnail file)
  updateArticle: (id, formData) => {
    // Laravel needs _method = PUT for FormData updates via POST
    if (formData instanceof FormData && !formData.has("_method")) {
      formData.append("_method", "PUT");
    }
    return http.post(`/api/v1/articles/${id}`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  },

  // Delete article
  deleteArticle: (id) => {
    return http.delete(`/api/v1/articles/${id}`);
  },

  // Toggle status
  toggleArticleStatus: (id) => {
    return http.patch(`/api/v1/articles/${id}/toggle-status`);
  },

  // --- Category APIs ---
  getCategories: (params = {}) => {
    return http.get("/api/v1/article-categories", { params });
  },

  getCategoryAll: () => {
    return http.get("/api/v1/article-categories/all");
  },

  getCategoryById: (id) => {
    return http.get(`/api/v1/article-categories/${id}`);
  },

  createCategory: (data) => {
    return http.post("/api/v1/article-categories", data);
  },

  updateCategory: (id, data) => {
    return http.put(`/api/v1/article-categories/${id}`, data);
  },

  deleteCategory: (id) => {
    return http.delete(`/api/v1/article-categories/${id}`);
  },

  toggleCategoryStatus: (id) => {
    return http.patch(`/api/v1/article-categories/${id}/toggle-status`);
  },

  // --- Public APIs for Residents ---
  getPublicArticles: (params = {}) => {
    return http.get("/api/v1/public/articles", { params });
  },

  getPublicArticleById: (id) => {
    return http.get(`/api/v1/public/articles/${id}`);
  },
};

// Helper functions for handling API responses
export const articleHelpers = {
  // Extract data from API response
  extractArticleData: (response) => {
    if (response.data && response.data.success) {
      return response.data.data;
    }
    throw new Error(response.data?.message || 'Unknown error occurred');
  },

  // Extract paginated article list from API response
  extractArticleList: (response) => {
    console.log('response', response)
    if (response.data && response.success) {
      const data = response.data;
      return {
        articles: data.data || [],
        pagination: {
          current: data.current_page,
          pageSize: data.per_page,
          total: data.total,
          from: data.from,
          to: data.to,
          lastPage: data.last_page,
        }
      };
    }
    throw new Error(response.data?.message || 'Unknown error occurred');
  },

  // Extract category list
  extractCategoryList: (response) => {
    if (response.data && response.data.success) {
       return response.data.data;
    }
    throw new Error(response.data?.message || 'Unknown error occurred');
  }
};

// Error handling wrapper standard in project
export const withErrorHandling = (apiFunction) => {
  return async (...args) => {
    try {
      const response = await apiFunction(...args);
      return response.data;
    } catch (error) {
      console.error('API Error:', error);
      if (error.response) {
        const { status, data } = error.response;
        switch (status) {
          case 400: throw new Error(data.message || 'Dữ liệu không hợp lệ');
          case 401: throw new Error('Bạn cần đăng nhập để thực hiện thao tác này');
          case 403: throw new Error('Bạn không có quyền thực hiện thao tác này');
          case 404: throw new Error('Không tìm thấy dữ liệu yêu cầu');
          case 422: throw new Error(data.message || 'Dữ liệu không hợp lệ');
          case 500: throw new Error(data.message || "Lỗi server, vui lòng thử lại sau");
          default: throw new Error(data.message || 'Đã có lỗi xảy ra');
        }
      } else if (error.request) {
        throw new Error('Không thể kết nối đến server');
      } else {
        throw new Error(error.message || 'Đã có lỗi xảy ra');
      }
    }
  };
};

export const articleAPIWithErrorHandling = {
  getArticles: withErrorHandling(articleAPI.getArticles),
  getArticleById: withErrorHandling(articleAPI.getArticleById),
  createArticle: withErrorHandling(articleAPI.createArticle),
  updateArticle: withErrorHandling(articleAPI.updateArticle),
  deleteArticle: withErrorHandling(articleAPI.deleteArticle),
  toggleArticleStatus: withErrorHandling(articleAPI.toggleArticleStatus),
  
  // Category wrapped
  getCategories: withErrorHandling(articleAPI.getCategories),
  getCategoryAll: withErrorHandling(articleAPI.getCategoryAll),
  createCategory: withErrorHandling(articleAPI.createCategory),
  updateCategory: withErrorHandling(articleAPI.updateCategory),
  deleteCategory: withErrorHandling(articleAPI.deleteCategory),
  toggleCategoryStatus: withErrorHandling(articleAPI.toggleCategoryStatus),

  // Public
  getPublicArticles: withErrorHandling(articleAPI.getPublicArticles),
  getPublicArticleById: withErrorHandling(articleAPI.getPublicArticleById),
};

export default articleAPI;
